import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, RefreshCcw, MessageSquare, ClipboardList, PenSquare, Plus, Save, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("align_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("assessments");
  const [error, setError] = useState("");

  const load = async (validateOnly = false) => {
    setError("");
    try {
      // simple ping: hit assessments list (cheapest)
      await axios.get(`${API}/assessment/admin/list`, {
        headers: { "X-Admin-Token": token },
      });
      setAuthed(true);
      localStorage.setItem("align_admin_token", token);
    } catch (e) {
      setError(e.response?.status === 401 ? "Invalid admin token." : "Failed to load.");
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <main data-testid="page-admin" className="pt-32 min-h-screen bg-[var(--bone)]">
        <section className="max-w-md mx-auto px-6">
          <div className="flex items-center gap-3 text-[var(--navy-900)]">
            <Lock size={20} /> <span className="eyebrow">Admin Access</span>
          </div>
          <h1 className="font-display text-[32px] text-[var(--navy-900)] mt-4">Leadership Dashboard</h1>
          <p className="text-[14px] text-[var(--muted)] mt-2">Enter your admin token to continue.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="w-full mt-6 border-b border-[var(--navy-900)]/25 bg-transparent outline-none py-3 text-[16px] focus:border-[var(--navy-900)]"
            placeholder="Admin token"
          />
          <button data-testid="admin-login-btn" onClick={() => load()} className="btn-primary mt-6 px-7 py-3 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold">
            Unlock
          </button>
          {error && <div className="mt-4 text-red-700 text-[14px]">{error}</div>}
        </section>
      </main>
    );
  }

  return (
    <main data-testid="page-admin" className="pt-28 pb-20 min-h-screen bg-[var(--bone)]">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between">
          <div>
            <div className="eyebrow">Leadership Dashboard</div>
            <h1 className="font-display text-[36px] md:text-[44px] text-[var(--navy-900)] mt-3">Insights</h1>
          </div>
        </div>

        {/* Tab nav */}
        <div className="mt-8 flex gap-1 border-b border-[rgba(10,31,68,0.1)]">
          {[
            { id: "assessments", label: "Assessments", I: ClipboardList },
            { id: "chats", label: "Chat Insights", I: MessageSquare },
            { id: "posts", label: "Blog Posts", I: PenSquare },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-[12px] tracking-[0.22em] uppercase font-semibold transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? "border-[var(--navy-900)] text-[var(--navy-900)]"
                  : "border-transparent text-[var(--muted)] hover:text-[var(--navy-900)]"
              }`}
            >
              <t.I size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "assessments" && <AssessmentsPanel token={token} />}
          {tab === "chats" && <ChatsPanel token={token} />}
          {tab === "posts" && <PostsPanel token={token} />}
        </div>
      </section>
    </main>
  );
}

function AssessmentsPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/assessment/admin/list`, {
        headers: { "X-Admin-Token": token },
      });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="assessments-panel">
      <div className="flex justify-between items-baseline">
        <p className="text-[14px] text-[var(--muted)]">{rows.length} total · most recent first</p>
        <button onClick={load} data-testid="assessments-refresh" className="btn-ghost px-5 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2">
          <RefreshCcw size={14} /> Refresh
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {loading && <div className="text-[var(--muted)]">Loading…</div>}
        {!loading && rows.length === 0 && <div className="text-[var(--muted)]">No submissions yet.</div>}
        {rows.map((r) => (
          <details key={r.id} data-testid={`admin-row-${r.id}`} className="bg-white border border-[rgba(10,31,68,0.08)] p-6">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
              <div>
                <div className="font-display text-[20px] text-[var(--navy-900)]">
                  {r.lead.name} <span className="text-[14px] text-[var(--muted)]">· {r.lead.role} at {r.lead.company}</span>
                </div>
                <div className="text-[12px] text-[var(--muted)] mt-1">
                  {r.lead.work_email} · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-[32px] text-[var(--navy-900)] leading-none">
                  {r.scores.overall}<span className="text-[14px] text-[var(--muted)]">/100</span>
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--gold)] mt-1">
                  {r.report?.retention_risk || "—"} risk
                </div>
              </div>
            </summary>
            <div className="mt-6 grid md:grid-cols-5 gap-4">
              {Object.entries(r.scores.pillars).map(([k, p]) => (
                <div key={k} className="border-l border-[var(--navy-900)]/10 pl-3">
                  <div className="font-display text-[24px] text-[var(--navy-900)]">{k}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--muted)]">{p.name}</div>
                  <div className="text-[16px] text-[var(--navy-900)] font-semibold mt-1">{p.score}/100</div>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-3 text-[14px] text-[var(--navy-900)] leading-relaxed">
              <div><span className="eyebrow block">Headline</span><div className="mt-1">{r.report?.headline}</div></div>
              <div><span className="eyebrow block">Recommended Intervention</span><div className="mt-1">{r.report?.recommended_intervention}</div></div>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function ChatsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/chats`, {
        headers: { "X-Admin-Token": token },
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !data) return <div className="text-[var(--muted)]">Loading…</div>;

  const sessions = data.sessions.filter((s) =>
    !filter ||
    (s.first_user_message && s.first_user_message.toLowerCase().includes(filter.toLowerCase())) ||
    (s.last_user_message && s.last_user_message.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div data-testid="chats-panel">
      {/* stat tiles */}
      <div className="grid sm:grid-cols-3 gap-px bg-[rgba(10,31,68,0.08)] mb-8">
        {[
          { n: data.stats.total_sessions, t: "Total conversations" },
          { n: data.stats.total_user_messages, t: "Visitor messages" },
          { n: data.stats.avg_messages_per_session, t: "Avg messages / session" },
        ].map((s) => (
          <div key={s.t} className="bg-white p-6">
            <div className="font-display text-[40px] text-[var(--navy-900)] leading-none">{s.n}</div>
            <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[var(--muted)]">{s.t}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <input
          data-testid="chats-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by query keyword (e.g., 'turnover', 'AI', 'GenZ')…"
          className="flex-1 min-w-[260px] border-b border-[var(--navy-900)]/25 bg-transparent outline-none py-2 text-[14px] focus:border-[var(--navy-900)]"
        />
        <button onClick={load} data-testid="chats-refresh" className="btn-ghost px-5 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2">
          <RefreshCcw size={14} /> Refresh
        </button>
      </div>

      <p className="text-[13px] text-[var(--muted)] mb-4">
        {sessions.length} of {data.sessions.length} conversations shown · most recent first
      </p>

      {sessions.length === 0 && (
        <div className="text-[var(--muted)] text-[14px]">
          {filter ? "No conversations match this filter." : "No chat conversations yet."}
        </div>
      )}

      <div className="space-y-3">
        {sessions.map((s) => (
          <details key={s.session_id} data-testid={`chat-session-${s.session_id}`} className="bg-white border border-[rgba(10,31,68,0.08)]">
            <summary className="cursor-pointer list-none p-5 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--gold)] font-semibold">
                  First question
                </div>
                <div className="font-display text-[18px] text-[var(--navy-900)] leading-snug mt-1 truncate">
                  {s.first_user_message || <em className="text-[var(--muted)]">(no visitor message)</em>}
                </div>
                <div className="text-[12px] text-[var(--muted)] mt-2 truncate">
                  {s.last_user_message && s.last_user_message !== s.first_user_message
                    ? `Last: ${s.last_user_message}`
                    : null}
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-2">
                  {s.user_message_count} question{s.user_message_count === 1 ? "" : "s"} · {s.updated_at ? new Date(s.updated_at).toLocaleString() : "—"}
                </div>
              </div>
            </summary>

            <div className="px-5 pb-5 pt-1 border-t border-[rgba(10,31,68,0.08)] space-y-3 max-h-[400px] overflow-y-auto">
              {s.messages.map((m, i) => (
                <div key={m.ts ? `${m.ts}-${m.role}` : `${s.session_id}-${i}`} className={`text-[13px] leading-relaxed p-3 ${
                  m.role === "user"
                    ? "bg-[var(--navy-50)] border-l-2 border-[var(--navy-700)]"
                    : "bg-[var(--bone)] border-l-2 border-[var(--gold)]"
                }`}>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--muted)] mb-1 font-semibold">
                    {m.role === "user" ? "Visitor" : "Ask A.L.I.G.N."}
                  </div>
                  <div className="text-[var(--navy-900)] whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}


/* ──────────────── BLOG POSTS PANEL ────────────────
   Lightweight CMS surface for Donja:
   - list all posts (drafts + published)
   - create new post (title + format + body + status)
   - edit / publish / unpublish / delete existing posts
   - markdown body editor (plain textarea — preview is the live /blog/:slug page)
*/
function PostsPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | postObject
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/blog/posts?include_drafts=true`, {
        headers: { "X-Admin-Token": token },
      });
      setRows(data);
    } catch (e) {
      setError("Failed to load posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/blog/posts/${post.id}`, {
        headers: { "X-Admin-Token": token },
      });
      await load();
    } catch (e) {
      setError("Delete failed.");
    }
  };

  return (
    <div data-testid="posts-panel">
      <div className="flex justify-between items-baseline flex-wrap gap-3">
        <p className="text-[14px] text-[var(--muted)]">{rows.length} posts · most recent first</p>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="posts-refresh"
            className="btn-ghost px-5 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
          <button
            onClick={() => setEditing("new")}
            data-testid="posts-new"
            className="btn-primary px-5 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2"
          >
            <Plus size={14} /> New Post
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 text-[13px] text-red-700" data-testid="posts-error">
          {error}
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading && <div className="text-[var(--muted)]">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-[var(--muted)]">No posts yet. Click "New Post" to write the first one.</div>
        )}
        {rows.map((p) => (
          <div
            key={p.id}
            data-testid={`post-row-${p.slug}`}
            className="bg-white border border-[rgba(10,31,68,0.08)] p-5 flex flex-wrap items-center gap-4"
          >
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className="text-[9px] tracking-[0.32em] uppercase font-bold"
                  style={{ color: p.format === "essay" ? "var(--gold)" : "var(--accent-gold)" }}
                >
                  {p.format === "essay" ? "Essay" : "Field Note"}
                </span>
                <span
                  className="text-[9px] tracking-[0.28em] uppercase font-bold px-2 py-0.5 rounded"
                  style={
                    p.status === "published"
                      ? { background: "rgba(14,139,78,0.12)", color: "var(--gold)" }
                      : { background: "rgba(10,31,68,0.06)", color: "var(--muted)" }
                  }
                >
                  {p.status}
                </span>
                <span className="text-[11px] text-[var(--muted)]">/blog/{p.slug}</span>
              </div>
              <h3 className="font-display text-[20px] text-[var(--navy-900)] mt-1.5">{p.title}</h3>
              {p.subtitle && (
                <p className="text-[13px] text-[var(--muted)] mt-1 max-w-2xl">{p.subtitle}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(p)}
                data-testid={`post-edit-${p.slug}`}
                className="btn-ghost px-4 py-2 rounded-full text-[10px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2"
              >
                <PenSquare size={12} /> Edit
              </button>
              <button
                onClick={() => remove(p)}
                data-testid={`post-delete-${p.slug}`}
                className="px-4 py-2 rounded-full text-[10px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2 text-red-700 border border-red-300 hover:bg-red-50"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <PostEditor
          token={token}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function PostEditor({ token, initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState({
    title: initial?.title || "",
    slug: initial?.slug || "",
    subtitle: initial?.subtitle || "",
    body: initial?.body || "",
    format: initial?.format || "essay",
    hero_image_url: initial?.hero_image_url || "",
    tags: (initial?.tags || []).join(", "),
    status: initial?.status || "draft",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      subtitle: form.subtitle.trim(),
      body: form.body,
      format: form.format,
      hero_image_url: form.hero_image_url.trim(),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: form.status,
    };
    try {
      if (isNew) {
        await axios.post(`${API}/blog/posts`, payload, {
          headers: { "X-Admin-Token": token },
        });
      } else {
        await axios.patch(`${API}/blog/posts/${initial.id}`, payload, {
          headers: { "X-Admin-Token": token },
        });
      }
      await onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="post-editor"
      className="fixed inset-0 z-[1000] bg-[rgba(5,13,28,0.55)] flex items-start justify-center p-6 overflow-y-auto"
    >
      <div className="bg-[var(--bone)] border border-[rgba(10,31,68,0.10)] max-w-3xl w-full my-10 shadow-2xl">
        <div className="flex items-center justify-between px-7 py-5 border-b border-[rgba(10,31,68,0.08)]">
          <div className="font-display text-[22px] text-[var(--navy-900)]">
            {isNew ? "New Post" : "Edit Post"}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="post-editor-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-7 space-y-5">
          <Field label="Title">
            <input
              data-testid="post-field-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className={inputCls}
              placeholder="The Year Strategy Stopped Saving Us"
            />
          </Field>
          <Field label="Subtitle (optional)">
            <input
              data-testid="post-field-subtitle"
              value={form.subtitle}
              onChange={(e) => update("subtitle", e.target.value)}
              className={inputCls}
              placeholder="A short sentence that previews the piece on /blog"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Format">
              <select
                data-testid="post-field-format"
                value={form.format}
                onChange={(e) => update("format", e.target.value)}
                className={inputCls}
              >
                <option value="essay">Essay (long-form)</option>
                <option value="fieldnote">Field Note (short)</option>
              </select>
            </Field>
            <Field label="Status">
              <select
                data-testid="post-field-status"
                value={form.status}
                onChange={(e) => update("status", e.target.value)}
                className={inputCls}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </Field>
          </div>
          <Field label="URL Slug (optional — auto-generated from title if blank)">
            <input
              data-testid="post-field-slug"
              value={form.slug}
              onChange={(e) => update("slug", e.target.value)}
              className={inputCls}
              placeholder="e.g. complexity-is-not-your-problem"
            />
          </Field>
          <Field label="Hero Image URL (optional)">
            <input
              data-testid="post-field-hero"
              value={form.hero_image_url}
              onChange={(e) => update("hero_image_url", e.target.value)}
              className={inputCls}
              placeholder="https://images.unsplash.com/..."
            />
          </Field>
          <Field label="Tags (comma-separated)">
            <input
              data-testid="post-field-tags"
              value={form.tags}
              onChange={(e) => update("tags", e.target.value)}
              className={inputCls}
              placeholder="Workforce, AI, Operations"
            />
          </Field>
          <Field label="Body (Markdown supported — **bold**, *italic*, ## subheads, lists, links, > quotes)">
            <textarea
              data-testid="post-field-body"
              value={form.body}
              onChange={(e) => update("body", e.target.value)}
              rows={16}
              className={`${inputCls} font-mono text-[13px] leading-[1.6]`}
              placeholder="## Opening hook&#10;&#10;Plain paragraphs of text separated by blank lines.&#10;&#10;Use **bold** and *italic* for emphasis."
            />
          </Field>

          {error && <div className="text-[13px] text-red-700">{error}</div>}

          <div className="flex justify-end gap-3 pt-3 border-t border-[rgba(10,31,68,0.08)]">
            <button
              onClick={onClose}
              data-testid="post-editor-cancel"
              className="btn-ghost px-5 py-2.5 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="post-editor-save"
              className="btn-primary px-6 py-2.5 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Save size={12} /> {saving ? "Saving…" : isNew ? "Create" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--muted)] font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full px-4 py-3 bg-white border border-[rgba(10,31,68,0.12)] text-[14px] text-[var(--navy-900)] outline-none focus:border-[var(--navy-900)] transition-colors rounded-sm";
